import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { callGetWebservice } from '../hooks/api';
import { decryptMallToken, loadingView } from '../utils/Utilities';
import '../assets/css/MallRestaurants.css';

const MallRestaurants = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [restaurants, setRestaurants] = useState([]);
    const [mallInfo, setMallInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) {
            setError('Invalid URL');
            setLoading(false);
            return;
        }

        const decoded = decryptMallToken(token);
        if (decoded && decoded.key) {
            callGetWebservice('malls-by-token', { token: token }, (response) => {
                if (response && response.data) {
                    setMallInfo(response.data);
                    setRestaurants(response.data.outlets || []);
                } else {
                    setError(response?.message || 'Failed to fetch restaurants.');
                }
                setLoading(false);
            });
        } else {
            setError('Invalid or expired QR code.');
            setLoading(false);
        }
    }, [token]);

    const handleOrderNow = (restaurant) => {
        if (restaurant.qrCode) {
            window.location.href = restaurant.qrCode;
        }
    };

    const handleGetDirection = (restaurant) => {
        if (restaurant.latitude && restaurant.longitude) {
            window.open(`https://www.google.com/maps/dir/?api=1&destination=${restaurant.latitude},${restaurant.longitude}`, '_blank');
        }
    };

    if (loading) return loadingView();
    if (error) {
        return (
            <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <div className="card shadow-lg border-0 text-center" style={{ borderRadius: '20px', maxWidth: '400px', width: '100%' }}>
                    <div className="card-body p-5">
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-4"
                            style={{ width: '80px', height: '80px', backgroundColor: '#fee2e2' }}
                        >
                            <svg width="40" height="40" fill="#ef4444" viewBox="0 0 16 16">
                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z" />
                            </svg>
                        </div>
                        <h4 className="mb-3" style={{ fontWeight: 'bold', color: '#333' }}>Oops!</h4>
                        <p className="text-muted mb-4">{error}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="btn px-4 py-2"
                            style={{ backgroundColor: '#ef4444', color: '#fff', borderRadius: '25px', fontWeight: '600', transition: 'background-color 0.2s' }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#dc2626'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#ef4444'}
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const mallName = mallInfo && mallInfo.mallKey 
                    ? mallInfo.mallKey.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
                    : 'Available Restaurants';

    return (
        <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', paddingBottom: '4rem' }}>
            {/* Hero Section */}
            <div className="mall-hero shadow-lg" style={{ paddingTop: '8rem' }}>
                <div className="container text-center position-relative" style={{ zIndex: 1 }}>
                    <span className="badge bg-white text-dark rounded-pill px-4 py-2 mb-4 shadow-sm" style={{ fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', fontSize: '0.8rem' }}>
                        Welcome To
                    </span>
                    <h1 style={{ fontWeight: '800', fontSize: '3.5rem', letterSpacing: '-1.5px', marginBottom: '1rem', textShadow: '0 4px 10px rgba(0,0,0,0.3)', color: '#ffffff' }}>
                        {mallName}
                    </h1>
                    <p className="mt-3" style={{ fontSize: '1.15rem', maxWidth: '650px', margin: '0 auto', opacity: '0.9', lineHeight: '1.6', color: '#f8fafc' }}>
                        Discover the best food and dining experiences tailored just for you. Order ahead to skip the lines or find your way instantly.
                    </p>
                </div>
            </div>

            {/* Content Section */}
            <div className="container" style={{ position: 'relative', zIndex: 2, paddingTop: '1rem' }}>
                {restaurants.length === 0 ? (
                    <div className="empty-state-card">
                        <div className="empty-icon-wrapper">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" />
                            </svg>
                        </div>
                        <h3 style={{ fontWeight: '800', color: '#1e293b', marginBottom: '1rem', fontSize: '1.8rem', letterSpacing: '-0.5px' }}>No Dining Options Yet</h3>
                        <p style={{ color: '#64748b', fontSize: '1.1rem', lineHeight: '1.6', marginBottom: '2rem', padding: '0 1rem' }}>
                            We couldn't find any active restaurants at this location right now. They might be temporarily closed or preparing to join us soon!
                        </p>
                        <button 
                            className="btn-premium-primary"
                            onClick={() => window.location.reload()}
                            style={{ padding: '0.8rem 2.5rem' }}
                        >
                            <i className="fas fa-sync-alt" style={{ marginRight: '8px' }}></i> Check Again
                        </button>
                    </div>
                ) : (
                    <div className="row g-4 justify-content-center">
                        {restaurants.map((rest, index) => (
                            <div className="col-12 col-md-6 col-lg-4" key={index}>
                                <div className="restaurant-card">
                                    <div className="card-image-wrapper">
                                        <img
                                            src={rest.image || 'https://via.placeholder.com/300x150?text=Restaurant'}
                                            alt={rest.merchant_name || rest.name}
                                            style={{ objectFit: 'contain', height: '110px', width: '100%', maxWidth: '220px', filter: 'drop-shadow(0px 10px 15px rgba(0,0,0,0.08))' }}
                                        />
                                    </div>
                                    <div className="card-body p-4 p-md-5 d-flex flex-column text-center">
                                        <h4 style={{ fontWeight: '800', color: '#0f172a', marginBottom: '0.75rem', letterSpacing: '-0.5px' }}>
                                            {rest.merchant_name || rest.name}
                                        </h4>
                                        <p className="text-slate-500 small mb-4 flex-grow-1" style={{ lineHeight: '1.6', color: '#64748b' }}>
                                            <i className="fas fa-map-pin text-danger opacity-75" style={{ marginRight: '8px' }}></i>
                                            {rest.address || 'Delicious food waiting for you.'}
                                        </p>
                                        <div className="mt-auto d-flex flex-column">
                                            <button
                                                onClick={() => handleOrderNow(rest)}
                                                className="btn-premium-primary w-100 d-flex justify-content-center align-items-center mb-3"
                                            >
                                                <i className="fas fa-shopping-bag" style={{ marginRight: '8px' }}></i> Order Now
                                            </button>
                                            <button
                                                onClick={() => handleGetDirection(rest)}
                                                className="btn-premium-outline w-100 d-flex justify-content-center align-items-center"
                                            >
                                                <i className="fas fa-location-arrow" style={{ marginRight: '8px' }}></i> Get Direction
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MallRestaurants;
